/**
 * @file   src\Pages\ProductSignUp.tsx
 * @brief  Product signup page.
 * @date   JUL, 2026
 * @author ZCO Engineer
 * @copyright (c) 2024, ZCO
 */

import { React, useState, useParams, useEffect } from '../utils/thirdpartyComponents'
import * as Yup from 'yup'
import { Button, Modal } from "react-bootstrap";
import { Link, useNavigate } from 'react-router-dom';
import CWInput from "src/components/CWInput";
import CWInputGroup from "src/components/CWInputGroup";
// import InputCheck from "src/components/CWChecBoxLabel"
import "src/styles/inner-page-styles.scss";
import TickIcon from "../assets/images/promot-code-tick.svg";
import { buildSignupSchema } from 'src/validations/signupSchema';
import { getActiveCountryCode } from 'src/utils/countryHelper';
import ProductSignUpVerificationModal from 'src/components/ProductSignUpVerificationModal';
import { EMAIL_MAX_LENGTH, PROFILE_NAME_MAX_LENGTH, REFERRAL_LENGTH, PHONE_MAX_LENGTH, PROMO_LENGTH, SIGNUP_FOR_FAMILYCODE_ERROR } from 'src/utils/constants';
import { pageURLs } from 'src/utils/pageUrls';
import {formatPhoneNumber, formatPhoneNumberforapipass, getFromLocalStorage, initProductSignUpShareFlow} from "../utils/helper";
import axios from '../config/axiosConfig';
import { MessageToaster } from "src/utils/toastUtil";
import {SignupApi} from "../utils/apiUrls";
import { ISignupData } from '../interfaces/generalInterface';
import { resetAuthState } from 'src/store/slices/authSlice';
import { useAppDispatch } from 'src/hooks';
import { getCompanySettings } from 'src/store/actions/settingsAction';
import SmallLoader from "src/components/SmallLoader";
import { Suspense, useTransition } from 'react';
import RadioButton from 'src/components/RadioButton';
import CWSelect from 'src/components/CWSelect';
import { CommonMessages } from 'src/messages/validationMessages';
const LazyCheckbox = React.lazy(() => import("src/components/CWChecBoxLabel"));

const ProductSignUp = () => {

    const [loading,setLoading] =  useState(false);
    const [pageLoading, setPageLoading] = useState(true);
    const params = useParams();
    const shareCode = params.shareCode || '';
    const carwashname = params.carwashname || localStorage.getItem('carwashname') || '';
          // Declare action dispatch.
          const dispatch = useAppDispatch();
    // Initialize states for formdata
    const [formData, setFormData] = useState<ISignupData>({
        carWashId:  0,
        name: '',
        phone: '',
        email: '',
        appliedReferralCode: '',
        receivesms:false,
        referralType:0
    })

    // Initialise states for errors
    const [errors, setErrors] = useState({
        name: '',
        phone: '',
        email: '',
        referralCode: '',
        receivesms: '',
    });

    // State to set non format phone number
    const [noformatPhone, setnoformatPhone] = useState('');

    // Instantiate MessageToaster
    const toaster = new MessageToaster();
    const navigate = useNavigate();

    // State to show modal
    const [showVerificationModal, setShowVerificationModal] = useState<boolean>(false);

    // State to enable tick icon on referral
    const [enableIcon, setEnableIcon] = useState<boolean>(false)
    const [couponSuccessMessage, setcouponSuccessMessage] = useState('');
    const [showCouponRemoveBtn , setCouponRemoveBtn] = useState(false);
    const [couponCodeFldDisable , setCouponCodeFldDisable] = useState(true);
    const [showRemoveCouponModal, SetRemoveCouponModal] = useState(false);
    const [redeemBtnDisabled, setRedeemBtnDisabled] = useState(true);
    const [appliedCoupon,setAppliedCoupon] = useState(false)
    const [showFamilyCodeModal, setShowFamilyCodeModal] = useState(false);
    // Handle verification modal
    const handleCloseVerificationModal = () => setShowVerificationModal(false);

    const [isPending, startTransition] = useTransition();
    
    const [discountCodeLabel,setdiscountCodeLabel] = useState('Promo Code');

    const handleFamilyCodeModalClose = () => setShowFamilyCodeModal(false);

    const handleFamilyCodeNo = () => {
        setShowFamilyCodeModal(false);
        toaster.toastError(CommonMessages.signup_for_familycode_error);
    };

    const handleFamilyCodeYes = async () => {
        setShowFamilyCodeModal(false);

        let carWashId = Number(localStorage.getItem('carWashId'));
        if (isNaN(carWashId) || carWashId === 0) {
            const request = { carwashname: carwashname };
            await fetchAppSettings(request);
            carWashId = Number(localStorage.getItem('carWashId'));
        }

        if (isNaN(carWashId) || carWashId === 0) {
            toaster.toastError("Could not process the request. Please try again later.");
            return false;
        }

        const requestPayload = {
            phoneNumber: formatPhoneNumberforapipass(formData.phone),
            carWashId: carWashId,
            carWashURL: carwashname,
            familyCode: formData.appliedReferralCode,
        };

        try {
            setLoading(true);
            const { data } = await axios.post(SignupApi.APPLY_FAMILY_CODE, requestPayload);
            if (data?.error === 0) {
                toaster.toastSuccess(data?.message);
                navigate("/"+carwashname+pageURLs.root);
                setShowFamilyCodeModal(false);
            } else {
                toaster.toastError(data?.message);
            }
        } catch (error: any) {
            toaster.toastError(error?.response?.data?.message || error?.message);
        } finally {
            setLoading(false);
        }
    };


      //   initial loading
  useEffect(() => {
    localStorage.removeItem('CARWASH_COMPANY_DATA');
    const initShareSignup = async () => {
      try {
        setPageLoading(true);
        if (localStorage.getItem('carWashId') === null || !localStorage.getItem('carWashId') || localStorage.getItem('carWashId') === undefined) {
          await dispatch(getCompanySettings({ carwashname }));
        }

        if (!shareCode) {
          toaster.toastError("Invalid subscription link.");
          navigate("/" + carwashname + pageURLs.root);
          return;
        }

        const canContinue = await initProductSignUpShareFlow(shareCode, carwashname, navigate, toaster);
        if (canContinue) {
          dispatch(resetAuthState());
        }
      } catch (error: any) {
        toaster.toastError(error?.response?.data?.message || error?.message || "Unable to load subscription.");
        navigate("/" + carwashname + pageURLs.root);
      } finally {
        setPageLoading(false);
      }
    };

    initShareSignup();
  }, []);

    // Triggers whenever input is entered
    const handleInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {

        // To allow only digits in phone number field
        if (event.target.name === "phone") {
            // Extract only digits from the input value
            const phone_value = event.target.value;
            if (!/^\d*$/.test(phone_value)) {
                event.target.value = phone_value.replace(/\D/g, '');
            }
        }

        const { name, value } = event.target

        // To remove validation when user enters input
        const keys = Object.keys(errors);
        if (keys.includes(name)) {
            setErrors({
                ...errors,
                [name]: ""
            })
        }

        // Enable tick icon for referral code when string length is greater or equal to 5
        if (name === "appliedReferralCode" && event.target.value) {
            //setEnableIcon(true)
            setRedeemBtnDisabled(false)
        } else if(name === "appliedReferralCode" && event.target.value == ""){
            //setEnableIcon(false);
            setRedeemBtnDisabled(true)
        }

        setFormData({
            ...formData,
            [name]: value,
        });

        // Change user enters Phone number in usa format
        if (name === 'phone') {
            const formattedValue = formatPhoneNumber(value);
            setFormData({
              ...formData,
              [name]: formattedValue,
            });
          }
    }

// Checkbox change handler
const handleCheckboxChange = (checked: boolean, name: string) => {

    // Remove validation error when the user updates the input
    if (errors[name as keyof typeof errors]) {
        setErrors({
            ...errors,
            [name]: ""
        });
    }

    // Update form data with the checkbox state
    setFormData({
        ...formData,
        [name]: checked,  // Assign the boolean value directly
    });
};
   

    // Triggers when sign up is done
    const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
        event.preventDefault()

        try {
            if(formData.appliedReferralCode!="" && appliedCoupon==false)
            {
               toaster.toastError("To use your discount code, click Apply before signing up.");  
               return false
            }
            setLoading(true);
            // Validate form data
            await buildSignupSchema(getActiveCountryCode()).validate(formData, { abortEarly: false });
            const newformData = { ...formData };
            // Modify formData to format phone number
            newformData.phone = formatPhoneNumberforapipass(newformData.phone);
            newformData.CarWashURL = carwashname;
            
            newformData.carWashId = Number(localStorage.getItem('carWashId'));
            if(isNaN(newformData.carWashId) || newformData.carWashId === 0) {
                const request = { carwashname : carwashname }
                await fetchAppSettings(request);
                newformData.carWashId = Number(localStorage.getItem('carWashId'));
            }
            setnoformatPhone(newformData.phone);
            if(newformData.carWashId === 0) {
                toaster.toastError("Could not process the request. Please try again later.");
                return false;
            }
            // API call to sign up
            try {
                toaster.toastDismiss();
                const { data } = await axios.post(SignupApi.SIGN_UP,newformData);
                if(data?.error === 0)
                {
                    // Show verification modal on signup
                    setShowVerificationModal(true);
                    //Clear errors
                    setErrors({
                        name: '',
                        phone: '',
                        email: '',
                        referralCode: '',
                        receivesms: '',
                    });
                }
                else if(data?.error === SIGNUP_FOR_FAMILYCODE_ERROR)
                {
                    setShowFamilyCodeModal(true);
                }
                else
                {
                    toaster.toastError(data?.message);
                    

                }
              } catch (error: any) {
                throw new Error(error?.response?.data?.message || error?.message);
              }
        } catch (error) {

            //If validation fails on submit, then errors are shown
            if (error instanceof Yup.ValidationError) {
                const newErrors: any = {};
                error.inner.forEach((err) => {
                    newErrors[err.path as keyof typeof formData] = err.message;
                });
                setErrors(newErrors);
            }
        }
        finally{
            setLoading(false);
        }
    }

    const fetchAppSettings = async (request : any) => {
        await dispatch(getCompanySettings(request));
    }


    const referralCoupnCheck = async () => {
        if(formData.appliedReferralCode == "") {
            toaster.toastError("Please enter valid referral code.");
            return false;
        }
        const requestPayload ={
            referralCode : formData.appliedReferralCode,
            CarWashURL : carwashname,
            referralType :formData.referralType
        }
        setLoading(true);
        await axios.post(SignupApi.VALIDATE_REFERRAL_CODE, requestPayload)
            .then(function (response : any){
                setLoading(false);
                setAppliedCoupon(true)
                if(response.data.error === 0) {
                    if(response.data.data.validCoupon === true) {
                        toaster.toastSuccess(response.data.message);
                        setEnableIcon(true)
                        setcouponSuccessMessage(response.data.message)
                        setCouponRemoveBtn(true)
                        setCouponCodeFldDisable(true)
                    } else{
                        toaster.toastError(response.data.message);
                        setEnableIcon(false)
                        setCouponRemoveBtn(false)
                        setCouponCodeFldDisable(false);
                        setFormData({
                            ...formData,
                            ['appliedReferralCode']: '',  // Assign the boolean value directly
                        });
                    }
                } else {
                    toaster.toastError(response.data.message);
                    setEnableIcon(false)
                    setCouponRemoveBtn(false)
                    setCouponCodeFldDisable(false);
                    setFormData({
                        ...formData,
                        ['appliedReferralCode']: '',  // Assign the boolean value directly
                    });
                }
            })

    };

    const referralCouponRemove = () => {
        setRedeemBtnDisabled(false)
        setCouponRemoveBtn(false)
        setCouponCodeFldDisable(false)
        SetRemoveCouponModal(false);
        setAppliedCoupon(false);

        setFormData({
            ...formData,
            ['appliedReferralCode']: '',  // Assign the boolean value directly
        });
    }

        // Function to handle coupon code type selection
        const handlePromoCodeTypeChange = (event: React.ChangeEvent<HTMLInputElement>) => {
            // setError(null)
            setCouponCodeFldDisable(false)
            setFormData({
                ...formData,
                ['referralType']: Number(event.target.value),
                // ['custpromocode']: '',
            });
            if(event.target.value == '1') {
                setdiscountCodeLabel('Referal Code')
            } else {
                setdiscountCodeLabel('Family Code')
            }
        };

    if (pageLoading || getFromLocalStorage('CARWASH_DATA')?.accessToken) {
        return <SmallLoader />;
    }
    
    return (

        <div className="common-content-wrap pb-0">
            <div className="common-header">
                <div className="pe-4 ps-4 pt-0 pb-0 h-100">
                    <div className="common-header-inner">
                        Sign Up
                    </div>
                </div>
            </div>

            <div className="inner-content-wrap signup-block">
                <div className="main-content-wrap">
                    <form onSubmit={(e) => { handleSubmit(e) }}>
                        <CWInput
                            id="name"
                            name="name"
                            value={formData.name}
                            placeholder="Name"
                            type="text"
                            label="Name"
                            isMandatory
                            autoFocus={true}
                            maxLength={PROFILE_NAME_MAX_LENGTH}
                            errorMessage={errors.name && errors.name}
                            onChange={handleInputChange}
                        />
                        <CWInput
                            id="phone"
                            name="phone"
                            value={formData.phone}
                            placeholder="Phone Number"
                            type="text"
                            inputmode="numeric" // This helps mobile devices to show a numeric keypad
                            label="Phone Number"
                            labelSuffix=" (Used for Sign In)"
                            isMandatory
                            maxLength={PHONE_MAX_LENGTH}
                            errorMessage={errors.phone && errors.phone}
                            onChange={handleInputChange}
                        />
                       
                        <CWInput
                            id="email"
                            name="email"
                            value={formData.email}
                            placeholder="Email Address"
                            inputmode="email"            // helps mobile keyboards
                            type="text"
                            label="Email Address"
                            maxLength={EMAIL_MAX_LENGTH}
                            isMandatory
                            errorMessage={errors.email && errors.email}
                            onChange={handleInputChange}
                        />
                    <div className="promo-radio mb-2">
                        <div className="form-group">
                        <label>Have a Discount Code?</label>
                        </div>
                            <div className="location-info-card d-flex">
                                <RadioButton 
                                    name="promoCodeType"
                                    label="Referal Code"
                                    checked = {formData.referralType == 1 ? true : false}
                                    value={1} 
                                    onChange={handlePromoCodeTypeChange} 
                                    // disabled={disabledCodeSelection}
                                /> 
                                <RadioButton 
                                    name="promoCodeType"
                                    label="Family Code" 
                                    checked = {formData.referralType == 2 ? true : false}
                                    value={2} 
                                    onChange={handlePromoCodeTypeChange} 
                                    // disabled={disabledCodeSelection}
                                />
                               
                            </div>
                        </div>
                        {/* <div className="promo-input position-relative">
                            <CWInputGroup
                                id="custpromocode"
                                name="custpromocode"
                                placeholder={discountCodeLabel}
                                type="text"
                                inputmode="numeric" // This helps mobile devices to show a numeric keypad
                                //label={"Have a "+discountCodeLabel+"?"}
                                value={formData.custpromocode}
                                errorMessage={errors.custpromocode && errors.custpromocode}
                                autoFocus={true}
                                maxLength={PROMO_LENGTH}
                                inputIcons={enableIcon ? TickIcon : ""}
                                onChange={handleInputChange}
                                showLabel = {false}
                            />
                        </div> */}

                    <div className="promo-input position-relative">
                        <CWInputGroup
                            id="appliedReferralCode"
                            name="appliedReferralCode"
                            placeholder={discountCodeLabel}
                            type="number"
                            label={discountCodeLabel}
                            errorMessage={errors.referralCode && errors.referralCode}
                            //inputIcons={enableIcon ? TickIcon : ""}
                            onChange={handleInputChange}
                            disabled={couponCodeFldDisable}
                            value={formData.appliedReferralCode}
                        />

                        {(!showCouponRemoveBtn ) &&<Button variant="primary"  className="primary-btn promo-btn" disabled={redeemBtnDisabled} onClick={() => referralCoupnCheck()}>Apply</Button>}
                        {(showCouponRemoveBtn ) && <Button variant="secondary" className="promo-btn"  onClick={referralCouponRemove}>Remove</Button> }
                        </div>
                        <Suspense fallback={<div>Loading checkbox...</div>}>
                            {isPending || (
                                <LazyCheckbox 
                                    id="receivesms"
                                    name="receivesms"
                                    label={
                                        <>
                                          I agree to receive recurring automated text messages at the phone number provided. Messages & data rates may apply. Messages frequency varies. Reply HELP for help and STOP to cancel.{' '}
                                          <Link to="https://www.carwashapp.com/privacy-policy.html" title="Privacy Policy" target="_blank" rel="noopener noreferrer">
                                            Privacy Policy
                                          </Link>{' '}
                                          and{' '}
                                          <Link to="https://www.carwashapp.com/terms-of-service.html" title="Terms of Service" target="_blank" rel="noopener noreferrer">
                                            Terms of Service
                                          </Link>.
                                        </>
                                      }
                                    // label='I agree to receive recurring automated text messages at the phone number provided. Msg & data rates may apply. Msg frequency varies. Reply HELP for help and STOP to cancel.'
                                    type="checkbox"
                                    checked={formData.receivesms}
                                    onChange={(e) => startTransition(() => handleCheckboxChange(e.target.checked, 'receivesms'))}
                                    errorMessage={errors.receivesms}
                                />
                            )}
                            
                        </Suspense>
                      
                        <Button type="submit" className="primary-btn btn-main" title='Sign Up'>Sign Up</Button>
                    </form>

                    <div className="signin-link text-center pb-5">
                        <p>Already have an account?</p>
                        <Link to={"/"+carwashname+pageURLs.root} title="Sign In" >Sign In</Link>
                        {/* <div className="privacy d-flex justify-content-center">
                            <Link to={"https://www.carwashapp.com/privacy-policy.html"} title="Privacy Policy" target='_blank'>Privacy Policy</Link>
                            <Link to={"https://www.carwashapp.com/terms-of-service.html"} title="Terms of Service" target='_blank'>Terms of Service</Link>
                        </div> */}
                    </div>
                </div>
            </div>

            <ProductSignUpVerificationModal
                show={showVerificationModal}
                onHide={handleCloseVerificationModal}
                onModalHide={handleCloseVerificationModal}
                phoneNumber = {noformatPhone}
            />
            <Modal
                show={showFamilyCodeModal}
                onHide={handleFamilyCodeModalClose}
                centered
                className='notification-modal'
            >
                <Modal.Body>
                This number is already registered. If you want to use a family code, please proceed
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="light" onClick={handleFamilyCodeNo}>No</Button>
                    <Button variant="primary" className="primary-btn" onClick={handleFamilyCodeYes}>Yes</Button>
                </Modal.Footer>
            </Modal>
            {loading ? <SmallLoader/> : ''}
        </div >
    )
}

export default ProductSignUp;
